import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Sparkles, Wrench, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/StatusPill";
import { api, apiError } from "@/lib/api";
import { useDictation } from "@/hooks/use-dictation";
import { fmtEUR, fmtDateTime } from "@/lib/constants";

const SEVERITY = { leicht: "bg-amber-500/15 text-amber-300 border-amber-500/30", mittel: "bg-orange-500/15 text-orange-300 border-orange-500/30", schwer: "bg-red-500/15 text-red-300 border-red-500/30" };
const REPAIR = { offen: "bg-red-500/15 text-red-300 border-red-500/30", in_arbeit: "bg-sky-500/15 text-sky-300 border-sky-500/30", repariert: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", akzeptiert: "bg-slate-500/20 text-slate-300 border-slate-500/30" };

export default function ConditionTab({ vehicle, reload }) {
  const [notes, setNotes] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ area: "", type: "kratzer", severity: "leicht", description: "", est_cost: "" });
  // Neue Notiz (tippen oder diktieren)
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const noteBaseRef = useRef("");
  const dictation = useDictation({
    onTranscript: (text) => setNewNote((noteBaseRef.current ? noteBaseRef.current.replace(/\s+$/, "") + " " : "") + text),
    onError: (msg) => toast.error(msg),
  });
  const toggleDictation = () => {
    if (dictation.status === "recording") { dictation.stop(); return; }
    if (dictation.status === "processing") return;
    noteBaseRef.current = newNote;
    dictation.start();
  };

  const loadNotes = () => api.get(`/vehicles/${vehicle.id}/notes`).then((r) => setNotes(r.data));

  useEffect(() => {
    api.get(`/vehicles/${vehicle.id}/notes`).then((r) => setNotes(r.data));
  }, [vehicle.id]);

  const saveNote = async () => {
    if (!newNote.trim()) return toast.warning("Bitte zuerst eine Notiz diktieren oder tippen.");
    if (dictation.status === "recording") dictation.stop();
    setSavingNote(true);
    try {
      const r = await api.post(`/vehicles/${vehicle.id}/notes`, { text: newNote.trim(), type: "text" });
      setNewNote("");
      await loadNotes();
      if (r.data.extraction) toast.success("Notiz gespeichert – KI hat die Infos ausgewertet.");
      else toast.success("Notiz gespeichert.");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingNote(false); }
  };

  const addDamage = async () => {
    if (!form.area) return toast.warning("Bitte Fahrzeugbereich angeben");
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/damages`, { ...form, est_cost: form.est_cost ? parseFloat(form.est_cost) : null });
      toast.success("Schaden dokumentiert");
      setOpen(false);
      setForm({ area: "", type: "kratzer", severity: "leicht", description: "", est_cost: "" });
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const setRepair = async (d, status) => {
    await api.patch(`/vehicles/${vehicle.id}/damages/${d.id}`, { repair_status: status });
    reload();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card className="lg:order-first">
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base font-display flex items-center gap-2"><Wrench className="h-4 w-4 text-primary" /> Schäden & Zustand</CardTitle>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="gap-1.5" data-testid="damage-add-button"><Plus className="h-3.5 w-3.5" /> Schaden</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Schaden dokumentieren</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Fahrzeugbereich</Label>
                    <Input value={form.area} onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))} placeholder="z.B. hinten_rechts" data-testid="damage-area-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Schadensart</Label>
                    <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                      <SelectTrigger data-testid="damage-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{["kratzer", "delle", "steinschlag", "felge_beschaedigt", "riss", "lackschaden", "innenraum", "technisch", "sonstiges"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Schweregrad</Label>
                    <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
                      <SelectTrigger data-testid="damage-severity-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{["leicht", "mittel", "schwer"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Geschätzte Kosten (EUR)</Label>
                    <Input type="number" value={form.est_cost} onChange={(e) => setForm((f) => ({ ...f, est_cost: e.target.value }))} data-testid="damage-cost-input" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Beschreibung</Label>
                  <Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="damage-desc-input" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={addDamage} disabled={busy} data-testid="damage-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Dokumentieren"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="space-y-2">
          {(vehicle.damages || []).length === 0 && <p className="text-sm text-muted-foreground">Keine Schäden dokumentiert.</p>}
          {(vehicle.damages || []).map((d) => (
            <div key={d.id} className="rounded-lg border border-border bg-secondary/30 p-3 space-y-1.5" data-testid={`damage-${d.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-medium">{d.description || `${d.type} – ${d.area}`}</div>
                <div className="flex gap-1.5">
                  <StatusPill label={d.severity} color={SEVERITY[d.severity]} />
                  <StatusPill label={d.repair_status?.replace("_", " ")} color={REPAIR[d.repair_status] || REPAIR.offen} />
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Bereich: {d.area} · Erkannt: {d.detected_by} {d.est_cost ? `· gesch. ${fmtEUR(d.est_cost)}` : ""} · {d.mentioned_in_listing ? "Wird im Inserat erwähnt" : "Nur intern"}
              </div>
              {d.repair_status !== "repariert" && (
                <div className="flex gap-1.5 pt-0.5">
                  {["in_arbeit", "repariert", "akzeptiert"].filter((x) => x !== d.repair_status).map((st) => (
                    <Button key={st} size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRepair(d, st)} data-testid={`damage-status-${d.id}-${st}`}>
                      {st.replace("_", " ")}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="order-first lg:order-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-display flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Notizen & KI-Extraktionen</CardTitle>
          <p className="text-xs text-muted-foreground">Infos zum Fahrzeug einfach einsprechen oder tippen – die KI wertet sie automatisch aus. Original-Notizen bleiben dauerhaft gespeichert.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Neue Notiz: diktieren oder tippen */}
          <div className="rounded-lg border border-primary/25 bg-primary/5 p-3 space-y-2" data-testid="condition-new-note-box">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="condition-note" className="text-xs">Neue Notiz (diktieren oder tippen)</Label>
              <Button variant="outline" size="sm"
                className={`h-7 gap-1.5 text-xs ${dictation.status === "recording" ? "border-red-500/50 text-red-300 hover:bg-red-500/10" : ""}`}
                onClick={toggleDictation} disabled={dictation.status === "processing"}
                data-testid="condition-voice-button">
                {dictation.status === "processing"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Mic className={`h-3.5 w-3.5 ${dictation.status === "recording" ? "animate-pulse text-red-400" : ""}`} />}
                {dictation.status === "recording" ? "Aufnahme stoppen" : dictation.status === "processing" ? "Transkribiere..." : "Diktieren"}
              </Button>
            </div>
            <Textarea id="condition-note" rows={3} value={newNote} onChange={(e) => setNewNote(e.target.value)}
              placeholder='z.B. "Zwei Schlüssel, Service neu, kleiner Kratzer hinten rechts, Winterräder dabei"'
              data-testid="condition-note-textarea" />
            {dictation.status === "recording" && (
              <p className="text-[11px] text-red-300 flex items-center gap-1" data-testid="condition-dictation-recording-hint">
                <span className="h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse" /> Aufnahme läuft – einfach sprechen, der Text erscheint im Feld.
              </p>
            )}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {dictation.mode === "browser" && dictation.status === "idle" ? (
                <p className="text-[10px] text-muted-foreground">Diktat über den Browser (Chrome, Edge, Safari) – kostenlos.</p>
              ) : <span />}
              <Button size="sm" className="gap-1.5" onClick={saveNote} disabled={savingNote} data-testid="condition-note-save-button">
                {savingNote ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {savingNote ? "KI wertet aus..." : "Speichern & KI auswerten"}
              </Button>
            </div>
          </div>
          {!notes ? <p className="text-sm text-muted-foreground">Wird geladen...</p> :
            notes.length === 0 ? <p className="text-sm text-muted-foreground">Keine Notizen vorhanden.</p> :
            notes.map((n) => (
              <div key={n.id} className="rounded-lg border border-border bg-secondary/30 p-3 space-y-1.5" data-testid={`note-${n.id}`}>
                <div className="text-xs text-muted-foreground">{fmtDateTime(n.created_at)} · {n.created_by} · {n.type === "voice" ? "Sprachnotiz" : "Textnotiz"} {n.applied ? "· übernommen" : ""}</div>
                <p className="text-sm italic text-muted-foreground">„{n.original_text}“</p>
                {n.extraction && (
                  <div className="text-xs space-y-0.5">
                    {n.extraction.extracted_fields.slice(0, 5).map((f, i) => (
                      <div key={i}><span className="text-muted-foreground">{f.field}:</span> {f.value}</div>
                    ))}
                    {n.extraction.open_questions?.length > 0 && (
                      <div className="text-sky-300 pt-1">Offene Fragen: {n.extraction.open_questions.join(" · ")}</div>
                    )}
                  </div>
                )}
              </div>
            ))}
        </CardContent>
      </Card>
    </div>
  );
}
